import { demoState } from './store.js';

type Handler = (payload: unknown) => void;

interface DemoSocketShape {
  on: (event: string, handler: Handler) => DemoSocketShape;
  off: (event: string, handler?: Handler) => DemoSocketShape;
  emit: (event: string, payload?: unknown) => DemoSocketShape;
  disconnect: () => void;
}

/**
 * Stand-in for a Socket.IO client. Internally subscribes to the demo state so
 * trip updates and driver-location ticks flow through the same UI hooks as the
 * real connection.
 */
export function createDemoSocket(): DemoSocketShape {
  const handlers = new Map<string, Set<Handler>>();
  const unsubs: Array<() => void> = [];

  const forward = (event: string) =>
    demoState.on(event, (payload) => {
      handlers.get(event)?.forEach((fn) => fn(payload));
    });

  unsubs.push(forward('trip:update'));
  unsubs.push(forward('trip:fare'));
  unsubs.push(forward('driver:location'));

  const socket: DemoSocketShape = {
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      set.add(handler);
      return socket;
    },
    off(event, handler) {
      if (!handler) handlers.delete(event);
      else handlers.get(event)?.delete(handler);
      return socket;
    },
    emit() {
      // The rider app only emits trip:join / trip:leave, which the demo can ignore.
      return socket;
    },
    disconnect() {
      handlers.clear();
      for (const u of unsubs) u();
    },
  };
  return socket;
}
